/**
 * Editing and deleting a touch — which is not one operation, because a touch
 * is not one kind of record.
 *
 * THE ROW IS SOMETIMES THE ORIGINAL AND SOMETIMES A SHADOW.
 *
 * A touch you typed into Log touch is its own fact: the engagements row IS the
 * record, and editing or deleting it does exactly what it says.
 *
 * A touch inferred from a note is a SHADOW of `contact_notes`, maintained by
 * private.sync_note_engagement(). That trigger upserts on the unique
 * `source_note_id` and fires on every later change to the note's
 * counts_as_touch, date, text or contact. So deleting the shadow directly
 * appears to work and then, the next time anyone edits that note, the touch
 * comes back -- with no message, weeks later, in a panel whose entire purpose
 * is being believed. Editing the shadow is worse: the next note edit
 * overwrites the text and the date from the note again.
 *
 * The correct delete for a note-derived touch is therefore not a delete at
 * all. It is `counts_as_touch = false` on the source note, which is also
 * exactly what the user means -- "this note was not contact" -- and which the
 * trigger honors by removing the engagement permanently.
 *
 * A touch from a Resend webhook is a record of what SOMEONE ELSE did. Editing
 * it is falsifying evidence, and there is no version of "they opened it, but
 * differently" that is true. It is refused rather than offered.
 *
 * UNKNOWN SOURCES ARE REFUSED, which is the same allowlist reasoning the
 * outreach clock uses one layer down. A source nobody has classified might be
 * another trigger's shadow, and the cost of guessing wrong is a silent
 * resurrection; the cost of refusing is a button that is grayed out with a
 * reason attached.
 */

import type { DataProvider } from "ra-core";
import type { Engagement } from "./types";
import { dateOnlyToTimestamp } from "./touchActions";

export type TouchPermission = {
  /** Whether the kind, date and note can be changed here. */
  canEdit: boolean;
  /** Whether this touch can be removed at all. */
  canDelete: boolean;
  /**
   * HOW to remove it. "engagement" deletes the row; "note" unticks
   * counts_as_touch on the source note and lets the trigger remove it.
   */
  deleteVia: "engagement" | "note" | null;
  /** Shown wherever an action is unavailable. Never empty when one is. */
  why: string;
};

const EDITABLE: TouchPermission = {
  canEdit: true,
  canDelete: true,
  deleteVia: "engagement",
  why: "",
};

export function touchPermission(
  engagement: Pick<Engagement, "source" | "source_note_id">,
): TouchPermission {
  switch (engagement.source) {
    // Ours, and the row is the original record.
    case "manual":
    case "import":
      return EDITABLE;

    // A shadow of contact_notes. Removable -- by saying the note was not
    // contact, which is durable -- but not editable, because the next note
    // edit would overwrite whatever was typed here.
    case "note":
      return {
        canEdit: false,
        canDelete: engagement.source_note_id != null,
        deleteVia: engagement.source_note_id != null ? "note" : null,
        why:
          engagement.source_note_id != null
            ? "This touch comes from a note. Removing it marks that note as " +
              "not contact; edit the note itself to change its text or date."
            : "This touch came from a note that no longer exists.",
      };

    // Ticking a task off is a real event, and the trigger fires only on the
    // null -> not-null transition, so removing this row stays removed. The
    // wording belongs to the task.
    case "task":
      return {
        canEdit: false,
        canDelete: true,
        deleteVia: "engagement",
        why: "This touch comes from a completed task. Edit the task to change what it says.",
      };

    // Somebody else's action, recorded by Resend.
    case "resend":
      return {
        canEdit: false,
        canDelete: false,
        deleteVia: null,
        why: "This records what the contact did, not what we did. It is evidence from Resend and cannot be edited or removed.",
      };

    default:
      return {
        canEdit: false,
        canDelete: false,
        deleteVia: null,
        why: "This touch was created by something this screen does not recognize, so it is left alone rather than risked.",
      };
  }
}

/** Change a touch you logged yourself. */
export async function updateTouch(
  dataProvider: DataProvider,
  engagement: Engagement,
  changes: { kind: string; note: string; date: string },
): Promise<void> {
  if (!touchPermission(engagement).canEdit) {
    throw new Error(touchPermission(engagement).why);
  }
  await dataProvider.update("engagements", {
    id: engagement.id,
    data: {
      kind: changes.kind,
      note: changes.note.trim() || null,
      // Same rule as logging one: today keeps the clock, an earlier day gets
      // noon. A date edit must not silently restamp the touch to right now.
      occurred_at: dateOnlyToTimestamp(changes.date),
    },
    previousData: engagement,
  });
}

/**
 * Remove a touch, by whichever route actually makes it stay gone.
 *
 * The note branch is the whole reason this is a function and not a call to
 * dataProvider.delete: for a note-derived touch, deleting the row is the
 * version that looks right and silently undoes itself later.
 */
export async function deleteTouch(
  dataProvider: DataProvider,
  engagement: Engagement,
): Promise<void> {
  const permission = touchPermission(engagement);
  if (!permission.canDelete) throw new Error(permission.why);

  if (permission.deleteVia === "note") {
    // The trigger deletes the engagement when this flips to false. Doing it
    // this way also records the judgment -- that the note was not contact --
    // rather than just erasing its consequence.
    await dataProvider.update("contact_notes", {
      id: engagement.source_note_id!,
      data: { counts_as_touch: false },
      previousData: { id: engagement.source_note_id! },
    });
    return;
  }

  await dataProvider.delete("engagements", {
    id: engagement.id,
    previousData: engagement,
  });
}

/** YYYY-MM-DD in local time, for seeding the edit dialog's date input. */
export function dateInputValue(occurredAt: string): string {
  const d = new Date(occurredAt);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}
